import { Injectable } from '@angular/core';
import { HelperService } from '../../services/utils/helper.service';
import { ApiService } from '../../services/api/api.service';
import { CompAccessService } from '../../services/compAccess/comp-access.service';
import { FacilityService } from '../facility/facility.service';
import { LoadingService } from '../shared/app-loading/loading.service';
import {
  OPERATION,
  COMPONENTS,
  CONSTANTS,
  EMAIL_TYPE,
  PHONE_TYPE,
  TABLE_CELL_WIDTH,
  SORT_TYPE,
  TABLE_SORT_ORDER,
} from '../../dataModels/constants';
import { CONFIG } from '../../dataModels/config';
import { ResourceManagerService } from '../../services/resourceManager/resource-manager.service';
import { LOV_KEYS } from '../../dataModels/lovs';
import { lastValueFrom } from 'rxjs';

/**
 * entity service
 */

@Injectable({
  providedIn: 'root',
})
export class EntityService {
  /**
   * Constructor to inject dependency
   * @param helperService instance of helperService
   * @param apiService instance of apiService
   * @param compAccessService instance of compAccessService
   * @param facilityService instance of facilityService
   * @param loadingService instance of loadingService
   * @param resourceManagerService instance of resourceManagerService
   */
  constructor(
    private helperService: HelperService,
    private apiService: ApiService,
    private compAccessService: CompAccessService,
    private facilityService: FacilityService,
    private loadingService: LoadingService,

    private resourceManagerService: ResourceManagerService
  ) {}

  /**
   * headers for entity
   */
  getEntityHeaders() {
    return [
      {
        text: 'Interface Entity Name',
        field: 'intfcEntityName',
        cellTemplate: 'LINK',
      },
      {
        text: 'Entity',
        field: 'intfcEntitySubTypeDescr',
      },
      {
        text: 'Status',
        field: 'intfcEntityStatus',
      },
    ];
  }

  /**
   * get list of entity
   */
  async getEntityList() {
    const request = {
      objects: [
        {
          name: OPERATION.INTERFACE.SEARCH_INTERFACE_ENTITY,
        },
      ],
    };

    let entityList = [];

    const url = this.helperService.isUserLoggedIn ? CONFIG.GET_INTFC_BUNDLE : CONFIG.GET_GLOBAL_HOMEBUNDLE;

    const result = await lastValueFrom(this.apiService.msPost(url, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      entityList = this.formatEntitylist(result.objects[0].json);
    }

    return entityList;
  }

  /**
   * format entity list recieved from the api
   */
  formatEntitylist(list) {
    const entityList = [];
    for (const item of list) {
      item.entityIdSeq = item.intfcEntity.intfcEntityIdSeq;
      item.intfcEntityName = item.intfcEntity.intfcEntityName;
      item.intfcEntityStatus = item.intfcEntity.intfcEntityStatus;
      item.intfcEntitySubTypeDescr = this.resourceManagerService.getLovColumnProperties(
        LOV_KEYS.ENTITY_SUB_TYPE,
        item.intfcEntity.intfcEntitySubType,
        'descr'
      );
      entityList.push(item);
    }
    return entityList;
  }

  /**
   * check if user has add system access
   */
  async checkAddCompAccess() {
    return await this.compAccessService.checkAccess([COMPONENTS.INTERFACE.ADD_ENTITY]);
  }

  /**
   * check if user has update system access
   */
  async checkUpdateCompAccess() {
    return await this.compAccessService.checkAccess([COMPONENTS.INTERFACE.UPDATE_ENTITY]);
  }

  /**
   * Create Interface Entity
   * @param generalInfo Systems general accordion form values
   * @param contactInfo Systems contact accordion list
   * @param comment Systems comment accordion form values
   */
  async createEntity(generalInfo, contactInfo, comment) {
    this.loadingService.displayLoader(`Creating Entity ${generalInfo.entityName.toUpperCase()}`);
    if(generalInfo.address){
    generalInfo.address.action =  CONSTANTS.ADD;
    }
    const request = {
      action: 'CREATE_INTERFACE_ENTITY',
      intfcEntityType: generalInfo.entityType,
      intfcEntitySubType: generalInfo.entitySubType,
      intfcEntityName: generalInfo.entityName.toUpperCase(),
      intfcEntityStatus: generalInfo.entityStatus,
      address: generalInfo.address,
      phoneList: this.facilityService.formPhoneAndFaxRequest(generalInfo, CONSTANTS.ADD),
      email: {
        action: CONSTANTS.ADD,
        emailType: EMAIL_TYPE.WORK,
        emailAddress: generalInfo.email,
      },
      intfcAccessType: generalInfo.intfcAccessType,
      intfcType: generalInfo.intfcType,
      sftpAccountId: generalInfo.sftpAccountId,
      sftpAccountIdStatus: generalInfo.sftpAccountIdStatus,
      contactList: this.facilityService.formContactListRequest(contactInfo, CONSTANTS.ADD),
      comment,
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_MS_INTERFACE_ENTITY, request));

    this.loadingService.hideLoader();

    return result;
  }

  /**
   * get details of the entity
   * @param intfcEntityIdSeq entity Id seq
   */
  async getEntityDetails(intfcEntityIdSeq) {
    this.loadingService.displayLoader('Getting details of the Entity');
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_INTERFACE_ENTITY,
          params: {
            intfcEntityIdSeq,
          },
        },
      ],
    };

    const entityData = {
      generalInfo: {
        entitySubType: '',
        entityName: '',
        entityStatus: '',
        address: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
          countyCode: '',
          municipalityCode: '',
          addressSeq: '',
          addressType: '',
        },
        phone: '',
        extn: '',
        fax: '',
        email: '',
        emailSeq: '',
        phoneSeq: '',
        faxSeq: '',
        intfcAccessType: '',
        intfcType: '',
        sftpAccountId: '',
        sftpAccountIdStatus: '',
        webSvcAccountId: '',
        webSvcAccountIdStatus: '',
        webSvcAccountIdType: '',
        facilityId : '',
      },
      contact: [],
      notesList: [],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_INTERFACE_BUNDLE, request));
    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      if (
        result.objects[0].json[0].intfcEntity && result.objects[0].json[0].intfcProfile
      
      ) {
        const entityInfo = result.objects[0].json[0];
        entityData.generalInfo.entitySubType = entityInfo.intfcEntity.intfcEntitySubType;
        entityData.generalInfo.entityName = entityInfo.intfcEntity.intfcEntityName;
        entityData.generalInfo.entityStatus = entityInfo.intfcEntity.intfcEntityStatus;
        entityData.generalInfo.intfcAccessType = entityInfo.intfcEntity.intfcAccessType;
        entityData.generalInfo.intfcType = entityInfo.intfcEntity.intfcType;

        //entityData.generalInfo.address = result.objects[0].json.address?.json?.length > 0 ? result.objects[0].json.address?.json[0] : {};

        //const phoneList = result.objects[0].json.phone.json;

        // for (const phone of phoneList) {
        //   if (phone.phoneType === PHONE_TYPE.WORK) {
        //     entityData.generalInfo.phone = phone.phoneNumber;
        //     entityData.generalInfo.extn = phone.phoneExtn;
        //     entityData.generalInfo.phoneSeq = phone.phoneSeq;
        //   } else if (phone.phoneType === PHONE_TYPE.FAX) {
        //     entityData.generalInfo.fax = phone.phoneNumber;
        //     entityData.generalInfo.faxSeq = phone.phoneSeq;
        //   }
        // }

        // const emailList = result.objects[0].json.email.json;

        // for (const email of emailList) {
        //   if (email.emailType === EMAIL_TYPE.WORK) {
        //     entityData.generalInfo.email = email.emailAddress;
        //     entityData.generalInfo.emailSeq = email.emailSeq;
        //   }
        // }

        entityData.generalInfo.sftpAccountId = entityInfo.intfcProfile.sftpAccountId;
        entityData.generalInfo.sftpAccountIdStatus = entityInfo.intfcProfile.sftpAccountIdStatus;
        entityData.generalInfo.webSvcAccountId = entityInfo.intfcProfile.webSvcAccountId;
        entityData.generalInfo.webSvcAccountIdStatus = entityInfo.intfcProfile.webSvcAccountIdStatus;
        entityData.generalInfo.webSvcAccountIdType = entityInfo.intfcProfile.webSvcAccountIdType;
        entityData.generalInfo.facilityId = entityInfo.intfcProfile.facilityId;
        // entityData.contact = this.facilityService.parseContact(result.objects[0].json.contact.json);

       // entityData.notesList = this.parseNotes(result.objects[0].json.note.json);
      }
    }
    this.loadingService.hideLoader();

    return entityData;
  }


   /**
   * Retrieve Address information
   * @param idSeq idSeq for which address will be obtained
   */
   async getEMAIL_Pnone(idSeq, addressType?) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_PHONE,
          params: {
            idSeq,
          },
        },
        {
          name: OPERATION.GLOBAL.GET_EMAIL,
          params: {
            idSeq,
          },
        },
        
      ],
    };

 //   const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));
 const facilityInfoResult = {} as any;
    const contactResult = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));
    if (contactResult.status === CONSTANTS.SUCCESS || contactResult.status === CONSTANTS.TRN_WARNING) {
      facilityInfoResult.status = CONSTANTS.SUCCESS;
      const response = {} as any;
      if (contactResult.objects) {
        for (const resultObj of contactResult.objects) {
          if (resultObj.name) {
            response[resultObj.name] = resultObj.json;
          }
        }
      }
     
      if (response[OPERATION.GLOBAL.GET_PHONE]) {
        facilityInfoResult.phoneList = response[OPERATION.GLOBAL.GET_PHONE];
      }
      if (response[OPERATION.GLOBAL.GET_EMAIL]) {
        facilityInfoResult.emailList = response[OPERATION.GLOBAL.GET_EMAIL];
      }
    }
    else {
      facilityInfoResult.status = CONSTANTS.TRN_ERROR;
    }


    return facilityInfoResult;
  }



  /**
   * Retrieve Address information
   * @param idSeq idSeq for which address will be obtained
   */
  async getAddress(idSeq, facilityId, addressType?) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_ADDRESS,
          params: {
            idSeq,
            facilityId
          },
        },
        
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const addressResult = {
      isError: false,
      address: {} as any,
      hasData: true,
    };

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length > 0 && result.objects[0].json && result.objects[0].json.length > 0) {
        addressResult.address = result.objects[0].json.filter(p=>p.addressType === addressType)[0];
      } else {
        addressResult.hasData = false;
      }
    } else {
      addressResult.isError = true;
    }

    return addressResult;
  }



  async getEntityNote(EntityId) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_NOTE,
          params: {
            idSeq : EntityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const entityNote = {
      isError: false,
      notesList: [],
    };

    if (this.helperService.isValidGetResult(result)) {
      entityNote.notesList = this.parseNotes(result.objects[0].json);
    } else {
      entityNote.isError = true;
    }

    return entityNote;
  }
  /**
   * parse noteList and decode comments
   */
  parseNotes(notesList) {
    if (notesList instanceof Array) {
      for (const note of notesList) {
        note.comments = this.helperService.decodeJsonString(note.comments);
      }
    } else {
      notesList = [];
    }
    return notesList;
  }

  /**
   * get headers for System
   */
  getNoteHeaders() {
    return [
      {
        text: 'User',
        field: 'userCreated',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Date Created',
        field: 'dateCreated',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
        sort: true,
        sortOrder: TABLE_SORT_ORDER.DESC,
      },
      {
        text: 'Comments',
        field: 'comments',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
    ];
  }


  async updateEnityAddress(address :any) {
    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_ADDRESS, address));

    this.loadingService.hideLoader();

    return result;
  }

  /**
   * Update interface entity
   * @param rowData entity row data
   * @param generalInfo Entity general accordion form values
   * @param contactInfo Entity contact accordion list
   * @param comment Entity comment accordion form values
   */
  async updateEntity(rowData, generalInfo, contactInfo, comment, oldGeneralInfo) {
    this.loadingService.displayLoader(`Updating Entity ${generalInfo.entityName.toUpperCase()}`);

    const request = {
      action: 'UPDATE_INTERFACE_ENTITY',
      intfcEntityIdSeq: rowData.entityIdSeq,
      intfcProfileSeq: rowData.intfcProfile.intfcProfileSeq,
      intfcEntityType: generalInfo.entityType,
      intfcEntitySubType: generalInfo.entitySubType,
      intfcEntityName: generalInfo.entityName.toUpperCase(),
      intfcEntityStatus: generalInfo.entityStatus,
      address: generalInfo.address,
      phoneList: this.facilityService.compareAndFormPhoneAndFaxRequest(oldGeneralInfo, generalInfo),
      email: {
        action: CONSTANTS.UPDATE,
        emailType: EMAIL_TYPE.WORK,
        emailAddress: generalInfo.email,
        emailSeq: generalInfo.emailSeq,
      },
      intfcAccessType: generalInfo.intfcAccessType,
      intfcType: generalInfo.intfcType,
      sftpAccountId: generalInfo.sftpAccountId,
      sftpAccountIdStatus: generalInfo.sftpAccountIdStatus,
      webSvcAccountId: generalInfo.webSvcAccountId,
      webSvcAccountIdStatus: generalInfo.webSvcAccountIdStatus,
      webSvcAccountIdType: generalInfo.webSvcAccountIdType,
      contactList: this.facilityService.formContactListRequest(contactInfo, CONSTANTS.UPDATE),
      comment,
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_MS_INTERFACE_ENTITY, request));

    this.loadingService.hideLoader();

    return result;
  }

  /**
   * reset IMS WebService Account key
   * @param contactList contact list or the entity
   * @param webServiceAccountId webService account id
   */
  async resetIMSWebService(contactList, webSvcAccountId) {
    this.loadingService.displayLoader(`Resetting WebService Account Id`);
    const request = {
      webSvcAccountId,
      emailList: contactList.map((x) => x.email),
    };
    this.loadingService.hideLoader();
    return await lastValueFrom(this.apiService.msPost(CONFIG.RESET_IMS_WEBSERVICE, request));
  }

  /**
   * header for facilities assigned table
   */
  getFacilityAssignedHeaders() {
    return [
      {
        text: 'Facility Id',
        field: 'facilityId',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.NUMBER,
        cellTemplate: 'LINK',
      },
      {
        text: 'Facility Name',
        field: 'facilityName',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Interface Enrollment Number',
        field: 'intfcFormId',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.NUMBER,
      },
      {
        text: 'Vendor',
        field: 'vendorName',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Interface Access Type',
        field: 'intfcAccessTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Interface Type',
        field: 'intfcTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Interface Status',
        field: 'status',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
    ];
  }

  /**
   * get facilities who have been assigned to the entity
   * @param intfcEntityIdSeq entityIdseq
   */
  async getAssignedFacilitiesForEntity(intfcEntityIdSeq) {
    const request = {
      objects: [
        {
          name: OPERATION.INTERFACE.GET_FACILITIES_ASSIGNED,
          params: {
            intfcEntityIdSeq,
          },
        },
      ],
    };

    let facilitiesAssignedList = [];

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects[0].json) {
      facilitiesAssignedList = result.objects[0].json;
      this.formatFacilitiesAssignedList(facilitiesAssignedList);
    }

    return facilitiesAssignedList;
  }

  /**
   * format list recieved from api
   */
  formatFacilitiesAssignedList(list) {
    list.forEach((item, i) => {
      item.serialNum = i;
      item.intfcAccessTypeDescr = this.resourceManagerService.getLovColumnProperties(
        LOV_KEYS.INTFC_ACCESS_TYPE,
        item.intfcAccessType,
        'descr'
      );
      item.intfcTypeDescr = this.resourceManagerService.getLovColumnProperties(LOV_KEYS.ENTITY_INTFC_TYPE, item.intfcType, 'descr');
    });
  }

  /**
   * check access
   */
  async checkAccess() {
    return this.compAccessService.checkAccess([COMPONENTS.INTERFACE.ADD_ENTITY, COMPONENTS.INTERFACE.VIEW_ENTITY]);
  }
}
